// Secret fields (invariant D7). A command input field that is a credential (a staff PIN, a device claim code) is marked
// with `secret(...)`. Nothing the server keeps may hold it or anything computed from it without a server-held key:
// the request fingerprint keys it with an HMAC, events and query outputs may not carry a secret field at all (contracts
// lint), and a command with a secret input records no event, so its input never reaches the event log or sync. A
// cloud command's output may hand a one-time credential to its caller (an invitation's accept link) in an optional
// top-level secret field: the caller receives it once, and the stored result a retry replays leaves it out
// (`dropSecrets`).
//
// Pure (no node:crypto): the declaration and the walk live here, beside the schemas devices also load; the keyed
// fingerprint that uses them is the server adapter's (`fingerprintInput`, http/fastify.ts).
import { z } from 'zod';

declare module 'zod' {
  interface GlobalMeta {
    /** A credential: never stored, logged or hashed without a server-held key (D7). */
    secret?: boolean;
  }
}

/** Marks a schema as a secret field. */
export const secret = <T extends z.ZodType>(schema: T): T => schema.meta({ secret: true });

export const isSecret = (schema: z.ZodType): boolean => z.globalRegistry.get(schema)?.secret === true;

type Def = { readonly type: string; readonly shape?: Record<string, z.ZodType>; readonly innerType?: z.ZodType; readonly element?: z.ZodType; readonly options?: readonly z.ZodType[]; readonly in?: z.ZodType };

const defOf = (schema: z.ZodType): Def => (schema as unknown as { _zod: { def: Def } })._zod.def;

/** The paths of every secret field a schema can carry ('*' for array items), e.g. [['pin']]. */
export function secretPaths(schema: z.ZodType, at: readonly string[] = []): string[][] {
  if (isSecret(schema)) return [[...at]];
  const d = defOf(schema);
  switch (d.type) {
    case 'object':
      return Object.entries(d.shape ?? {}).flatMap(([k, s]) => secretPaths(s, [...at, k]));
    case 'optional':
    case 'nullable':
    case 'default':
    case 'readonly':
      return d.innerType === undefined ? [] : secretPaths(d.innerType, at);
    case 'array':
      return d.element === undefined ? [] : secretPaths(d.element, [...at, '*']);
    case 'union':
      return (d.options ?? []).flatMap((o) => secretPaths(o, at));
    case 'pipe':
      return d.in === undefined ? [] : secretPaths(d.in, at);
    default:
      return [];
  }
}

/** A wire value without its secret top-level fields (what a command's stored result keeps of its output). */
export function dropSecrets(paths: readonly (readonly string[])[], value: unknown): unknown {
  if (paths.length === 0 || value === null || typeof value !== 'object') return value;
  const keys = new Set(paths.map((p) => p[0]));
  return Object.fromEntries(Object.entries(value).filter(([k]) => !keys.has(k)));
}

/** The secret fields of an output that a stored result cannot drop: anything but an optional top-level field. */
export function undroppableSecrets(schema: z.ZodType): string[][] {
  const shape = defOf(schema).shape ?? {};
  return secretPaths(schema).filter((p) => {
    const field = p.length === 1 && p[0] !== undefined ? shape[p[0]] : undefined;
    return field === undefined || defOf(field).type !== 'optional';
  });
}

/** A wire value with every secret field replaced by `replace(value)` (missing fields stay missing). */
export function maskSecrets(paths: readonly (readonly string[])[], value: unknown, replace: (secretValue: string) => string): unknown {
  const at = (v: unknown, path: readonly string[]): unknown => {
    if (path.length === 0) return typeof v === 'string' ? replace(v) : v === undefined || v === null ? v : replace(JSON.stringify(v));
    const [head, ...rest] = path;
    if (head === '*') return Array.isArray(v) ? v.map((x) => at(x, rest)) : v;
    if (v === null || typeof v !== 'object' || head === undefined || !(head in v)) return v;
    return { ...(v as Record<string, unknown>), [head]: at((v as Record<string, unknown>)[head], rest) };
  };
  return paths.reduce(at, value);
}
